
import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { selectUnitPageData } from "store/selectors/UnitPage";
import usePageViewTracking from "hooks/tracking/usePageViewTracking";
import { PAGE_TYPES } from "@sparefoot/tracking-events";
import { TrackingClient } from "@sparefoot/tracking-client";
import { Page } from "components/core/Page";
import Container from "components/core/Page/Container";
import { FacilityPageHeader } from "components/facility/FacilityPageHeaderV2";
import { formatUnitSize, formatUSD } from "utils/unitNameTrim";
import { Image } from "@sparefoot/react-commons";
import { H2, Paragraph, Text } from "components/core/Typography/Typography";
import { UnitIcon } from "components/molecules";
import { isEmpty } from 'lodash';
import classnames from "classnames";
import checkImage from "assets/images/check.svg";
import { UnitReservationFormV2 } from "components/facility/UnitReservationFormV2";
import { createBooking } from "store/modules/booking";
import { getLocalNumber } from "store/modules/phone";
import formatDate from "utils/formatDateReservation";
import { setCookie } from "utils/localStorageUtil";
import "./UnitPageV2.scss";

export default function UnitPageV2({ match }) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showAmenities, setShowAmenities] = useState(false);
  const [errorReservationMessages, setErrorReservationMessages] = useState(undefined);

  const dispatch = useDispatch();
  const { facilityId, unitId } = match.params;
  const {
    unit,
    facility
  } = useSelector(state => selectUnitPageData(state, { facilityId, unitId }));
  // Page View Tracking
  usePageViewTracking(PAGE_TYPES.RESERVE);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isScarce = unit?.quantity && unit.quantity <= 3;
  const {
    unitType,
    squareFootage,
    price,
    special,
    sizeText,
    typeText,
    tierName,
    reservationWindow,
    availableAmenities
  } = unit || {};
  const lowestPrice = price?.sparefoot || price?.regular;
  const { closures = [], } = facility || {};
  const pricePerSqFt = lowestPrice && squareFootage
    ? parseFloat((lowestPrice / squareFootage).toFixed(2))
    : null;
  const tierAndType = tierName ? `${tierName} ${typeText}` : typeText;

  // UI state
  const toggleCollapse = useCallback(() => setIsCollapsed(c => !c), []);
  const handleKeyDown = useCallback(e => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleCollapse();
    }
  }, [toggleCollapse]);

  // Handlers
  const handleFetchNumber = useCallback(async (city, state) => {
    const data = { path: `${city}/${state}` };
    const {
      payload: { result }
    } = await dispatch(getLocalNumber(data));
    return result;
  }, [dispatch]);

  const onSubmit = useCallback(async (data) => {
    const { alternateNames, moveInDate, ...restData } = data;
    const reservationData = {
      alternateFirstNames:
        alternateNames?.length > 0 &&
        alternateNames.map((name) => name.firstName),
      alternateLastNames:
        alternateNames?.length > 0 &&
        alternateNames.map((name) => name.lastName),
      moveInDate: formatDate(moveInDate),
      facilityId,
      unitId,
      ...restData
    };

    try {
      // Track before creating booking
      TrackingClient.trackReservationStarted({
        cart_products_count: 1,
        cart_value: lowestPrice,
        currency: "usd",
        products: unitId,
        unit: {
          currency: "usd",
          id: unitId,
          variant_size: sizeText,
          price: lowestPrice,
          quantity: 1
        },
        unit_id: unitId,
        unit_promo: special
      });

      const reservationRequest = {
        method: "POST",
        body: reservationData
      };

      const reservationDone = await dispatch(createBooking(reservationRequest));

      if (createBooking.rejected.match(reservationDone)) {
        setErrorReservationMessages(
          reservationDone?.error?.message ||
          "Unable to complete reservation. Please try again."
        );
        return;
      }

      const reservationPayload = reservationDone?.payload || {};
      const { result } = reservationPayload;
      const booking = reservationPayload?.entities?.bookings?.[result];
      const email = booking?.email;
      const bookingAuth = booking?.rawBooking?.bookingAuth;

      if (bookingAuth) {
        setCookie("jwt", bookingAuth);
      }

      if (result && result !== "errorBooking") {
        // Track reservation completed
        TrackingClient.trackReservationCompleted({
          currency: "usd",
          discounts: "",
          first_order_date: Date.now(),
          last_order_date: Date.now(),
          move_in_date: formatDate(moveInDate),
          promotion_code: special,
          reservation_confirmation: result,
          revenue: lowestPrice,
          tax: "",
          unit: {
            currency: "usd",
            id: unitId,
            variant_size: sizeText,
            price: lowestPrice,
            quantity: 1
          },
          unit_id: unitId,
          unit_promo: special,
          email: reservationData.email,
          first_name: reservationData.firstName,
          last_name: reservationData.lastName,
          phone: reservationData.phone
        });

        if (typeof window !== "undefined") {
          const bookingEmail = email || reservationData.email || "";
          window.location.href = `/reservation/confirmation/${result}/?email=${encodeURIComponent(bookingEmail)}`;
        }
      } else {
        const errorMessage =
          reservationPayload?.entities?.bookings?.result?.message ||
          reservationPayload?.message ||
          "Unable to complete reservation. Please try again.";
        setErrorReservationMessages(errorMessage);
      }
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
    }
  }, [dispatch, facilityId, unitId, lowestPrice, sizeText, special]);

  const primaryAmenities = useMemo(
    () => availableAmenities?.slice(0, 5) ?? [],
    [availableAmenities]
  );
  const additionalAmenities = useMemo(
    () => availableAmenities?.slice(5) ?? [],
    [availableAmenities]
  );

  // Pass all props to your summary card/form when you build them
  return (
    <Page className="unit-page-v2" noIndex>
      <Container>
        <div className="facility-details-container">
          {/* Breadcrumbs are intentionally omitted on the checkout/reserve page */}
          {facility && <FacilityPageHeader {...facility} />}
        </div>
        <div className="unit-reservation-page__container">
          <div className="unit-reservation-page__left">
            <div className="unit-summary-card">
              <div
                className="unit-summary-card__mobile-header"
                onClick={toggleCollapse}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={0}
              >
                <div
                  className="unit-summary-card__mobile-expand"
                >
                  <p className="collapse-expand-btn">
                    {isCollapsed ? 'EXPAND' : 'COLLAPSE'}
                  </p>
                  <span
                    className={classnames(
                      'collapse-expand-icon',
                      {
                        'collapse-expand-icon--expanded': !isCollapsed,
                      },
                    )}
                    aria-hidden="true"
                  >
                    <span className="collapse-expand-icon__horizontal" />
                    {isCollapsed && (
                      <span className="collapse-expand-icon__vertical" />
                    )}
                  </span>
                </div>
                <div
                  className={classnames(
                    'unit-summary-card__unit-info',
                    {
                      collapsed: !isCollapsed,
                      expanded: isCollapsed,
                    },
                  )}
                >
                  <div className="unit-summary-card__unit-header-info">
                    <div className="unit-summary-card__unit-header-text">
                      <Text
                        className="unit-size-label-description-mobile"
                        size="medium"
                      >
                        {formatUnitSize(unit?.sizeText)}{' '}
                        {tierAndType}{' '}
                      </Text>
                      {isScarce && (
                        <Text
                          className="scarcity-message"
                          last
                        >
                          ONLY {unit?.quantity} LEFT!
                        </Text>
                      )}
                    </div>
                    {special && (
                      <Paragraph
                        size="small"
                        className="unit-summary-card__promotion-mobile"
                        weight="ultraBold"
                        family="standard"
                      >
                        {unit?.special}
                      </Paragraph>
                    )}
                  </div>
                  <div className="unit-summary-card__mobile-price">
                    {formatUSD(lowestPrice)}
                  </div>
                </div>
              </div>
              <div
                className={classnames(
                  'unit-summary-card__selected-unit-card',
                  {
                    collapsed: isCollapsed,
                    expanded: !isCollapsed,
                  },
                )}
              >
                <div className="unit-summary-card__card-unit-information">
                  <div className="unit-summary-card__unit-header">
                    <div className="unit-summary-card__unit-header-info">
                      <Text className="unit-size-label-description">
                        {formatUnitSize(unit?.sizeText)}{' '}
                      </Text>
                      {pricePerSqFt && (
                        <span className="price-per-sqft">
                          {formatUSD(pricePerSqFt, { showDecimal: true })} / sqft
                        </span>
                      )}
                    </div>
                    <UnitIcon className="unit-summary-card__unit-icon" unitType={unitType} squareFootage={squareFootage} />
                  </div>
                  <div className="unit-summary-card__summary-bottom-card">
                    <div className="unit-summary-card__features-section">
                      <p className="amenities-heading">
                        Features
                      </p>
                      <div className="amenities-list">
                        {!isEmpty(availableAmenities) && (
                          <ul className="amenities">
                            {primaryAmenities
                              .map((amenity, i) => ({ amenity, uid: `amenity-${i}` }))
                              .map(({ amenity, uid }, index, array) => (
                                <React.Fragment key={uid}>
                                  <li className="amenities-item">
                                    {amenity}
                                  </li>

                                  {index <
                                    array.length - 1 && (
                                      <li className="amenities-elipses-dot" />
                                    )}
                                </React.Fragment>
                              ))}
                            {additionalAmenities.length > 0 && (
                                <React.Fragment>
                                  <li className="amenities-elipses-dot" />
                                  <button
                                    type="button"
                                    className="more-amenities-btn"
                                    onClick={() =>
                                      setShowAmenities(
                                        !showAmenities,
                                      )
                                    }
                                  >
                                    +{' '}
                                    {additionalAmenities.length}{' '}
                                    Amenities
                                  </button>
                                </React.Fragment>
                              )}
                          </ul>
                        )}
                        {showAmenities && (
                          <ul className="amenities-remaining-list">
                            {additionalAmenities
                              .map((amenity, i) => ({ amenity, uid: `amenity-remaining-${i}` }))
                              .map(({ amenity, uid }, index, array) => (
                                <React.Fragment key={uid}>
                                  <li
                                    className="amenities-remaining-list-item"
                                  >
                                    {amenity}
                                  </li>
                                  {index <
                                    array.length - 1 && (
                                      <li className="amenities-elipses-gray-dot" />
                                    )}
                                </React.Fragment>
                              ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    {special && (
                      <Paragraph
                        size="small"
                        className="unit-summary-card__promotion"
                        weight="ultraBold"
                      >
                        {special}
                      </Paragraph>
                    )}
                  </div>
                </div>
                <div className="unit-summary-card__card-price-information">
                  <div className="unit-summary-card__pricing-header">
                    <p className="monthly-unit-rent">Monthly Unit Rent</p>
                    <div className="unit-quantity-price-container">
                      {isScarce && (
                        <p className="units-left">ONLY {unit?.quantity} LEFT!</p>
                      )}
                      <p className="monthly-unit-rent-price">
                        {formatUSD(lowestPrice)}
                      </p>
                    </div>
                  </div>
                  {facility?.totalFees > 0 && (
                    <>
                      <div className="unit-summary-card__admin-fees">
                        <div className="admin-pricing-header">
                          <p className="admin-fees-label">
                            Admin Fees
                          </p>
                          <p className="admin-fees-value">
                            {formatUSD(
                              facility?.totalFees,
                            )}
                          </p>
                        </div>
                        <div className="pricing-description">
                          <span className="pricing_label">
                            The following fees shall
                            apply:
                          </span>
                          <ul className="pricing_items">
                            {facility?.adminFee >
                              0 && (
                                <li className="pricing_item">
                                  One-Time Admin
                                  Fee: $
                                  {
                                    facility?.adminFee
                                  }
                                </li>
                              )}
                            {facility?.lockFee >
                              0 && (
                                <li className="pricing_item">
                                  <Text
                                    color="black"
                                    size="smaller"
                                  >
                                    One-Time
                                    Lock Fee: $
                                    {
                                      facility?.lockFee
                                    }
                                  </Text>
                                </li>
                              )}
                          </ul>
                        </div>
                      </div>
                      <Paragraph
                        color="gray"
                        weight="standard"
                        className="unit-summary-card__admin-fee-description"
                      >
                        The one-time, non-refundable
                        admin fee is charged upon
                        signing your lease agreement.
                        This fee covers the costs
                        associated with processing your
                        application and setting up your
                        account and access credentials,
                        ensuring a smooth move-in
                        process.
                      </Paragraph>
                    </>
                  )}
                </div>
                <p className="unit-summary-card__payment-due-message">Payment due at move-in</p>
              </div>
            </div>
          </div>
          <div className="unit-reservation-page__right">
            <div className="reservation-form-card">
              <div className="header-container">
                <H2
                  size="medium"
                  className="checkoutpage-reservation-header"
                >
                  Reserve today to lock in this rate now
                </H2>
                <div className="check-with-text-container">
                  <div className="check-with-text">
                    <span
                      className="check-with-text__icon"
                      aria-hidden
                    >
                      <Image
                        src={checkImage}
                        alt="check"
                      />
                    </span>
                    <span className="check-with-text__label">
                      Risk free
                    </span>
                  </div>
                  <div className="check-with-text">
                    <span
                      className="check-with-text__icon"
                      aria-hidden
                    >
                      <Image
                        src={checkImage}
                        alt="check"
                      />
                    </span>
                    <span className="check-with-text__label">
                      Cancel anytime
                    </span>
                  </div>
                  <div className="check-with-text">
                    <span
                      className="check-with-text__icon"
                      aria-hidden
                    >
                      <Image
                        src={checkImage}
                        alt="check"
                      />
                    </span>
                    <span className="check-with-text__label">
                      No payment required
                    </span>
                  </div>
                </div>
              </div>
              <UnitReservationFormV2
                reservationWindow={reservationWindow ?? 0}
                closures={closures}
                onSubmit={onSubmit}
                unitPrice={lowestPrice}
                cityUrl={facility?.cityUrl}
                errorReservationMessages={errorReservationMessages}
                setErrorReservationMessages={setErrorReservationMessages}
                handleFetchNumber={handleFetchNumber}
              />
            </div>
          </div>
        </div>
      </Container>
    </Page>
  );
}

UnitPageV2.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      facilityId: PropTypes.string.isRequired,
      unitId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};
